export const TIPO_CAMBIO_USD_PREV = 16.00;
export const TIPO_CAMBIO_USD = 17.00;
export const TC_USD_CUTOFF_DATE = '2026-11-03';
export const TC_USD_CUTOFF_HOUR = 7;

const _tijuanaDateHourFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/Tijuana',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  hour12: false,
});

function _extractHourFromTimeHint(timeOrEvent) {
  if (timeOrEvent === null || timeOrEvent === undefined) return null;
  if (typeof timeOrEvent === 'number' && Number.isFinite(timeOrEvent)) {
    return timeOrEvent;
  }
  if (timeOrEvent instanceof Date) {
    if (isNaN(timeOrEvent.getTime())) return null;
    const parts = _tijuanaDateHourFormatter.formatToParts(timeOrEvent);
    const hourStr = parts.find((p) => p.type === 'hour')?.value;
    if (hourStr !== undefined) {
      return parseInt(hourStr, 10) % 24;
    }
    return null;
  }
  if (typeof timeOrEvent === 'object') {
    const rawTime =
      timeOrEvent.hora_pago ||
      timeOrEvent.hora_ingreso ||
      timeOrEvent.form_data?.hora_llegada ||
      timeOrEvent.form_data?.hora_admitido ||
      timeOrEvent.horaLlegada ||
      timeOrEvent.horaAdmitido ||
      timeOrEvent.start_time ||
      timeOrEvent.hora ||
      timeOrEvent.horaAgendada ||
      timeOrEvent.created_at ||
      '';
    return rawTime ? _extractHourFromTimeHint(rawTime) : null;
  }
  if (typeof timeOrEvent === 'string') {
    const s = timeOrEvent.trim();
    if (!s || s === '-') return null;
    if (s.includes('T') && (s.endsWith('Z') || /[+-]\d{2}:?\d{2}$/.test(s))) {
      const dt = new Date(s);
      if (!isNaN(dt.getTime())) {
        return _extractHourFromTimeHint(dt);
      }
    }
    const m = s.match(/(?:T|\s|^)(\d{1,2}):(\d{2})(?::\d{2})?\s*(a\.?\s*m\.?|p\.?\s*m\.?)?/i);
    if (m) {
      let h = parseInt(m[1], 10);
      const ampm = (m[3] || '').toLowerCase().replace(/[^ap]/g, '');
      if (ampm === 'p' && h < 12) h += 12;
      if (ampm === 'a' && h === 12) h = 0;
      return h % 24;
    }
  }
  return null;
}

/**
 * Devuelve el Tipo de Cambio USD aplicable:
 * - Antes del 3 de noviembre de 2026 a las 07:00 AM (America/Tijuana): $16.00 MXN/USD
 * - A partir del 3 de noviembre de 2026 desde las 07:00 AM: $17.00 MXN/USD
 */
export const getTipoCambioUsdForDateTime = (dateOrTimestamp, timeOrEvent = null) => {
  let target = dateOrTimestamp;
  if (!target && timeOrEvent && typeof timeOrEvent === 'object' && !(timeOrEvent instanceof Date)) {
    target =
      timeOrEvent.fecha_pago ||
      timeOrEvent.eventDate ||
      timeOrEvent.fecha ||
      timeOrEvent.form_data?.fecha ||
      timeOrEvent.start_time ||
      timeOrEvent.created_at ||
      null;
  }
  if (!target) return TIPO_CAMBIO_USD;

  if (target instanceof Date) {
    if (isNaN(target.getTime())) return TIPO_CAMBIO_USD;
    const parts = _tijuanaDateHourFormatter.formatToParts(target);
    const y = parts.find((p) => p.type === 'year')?.value || '';
    const m = parts.find((p) => p.type === 'month')?.value || '';
    const d = parts.find((p) => p.type === 'day')?.value || '';
    const h = parseInt(parts.find((p) => p.type === 'hour')?.value || '12', 10) % 24;
    const dateStr = `${y}-${m}-${d}`;
    if (dateStr < TC_USD_CUTOFF_DATE) return TIPO_CAMBIO_USD_PREV;
    if (dateStr > TC_USD_CUTOFF_DATE) return TIPO_CAMBIO_USD;
    return h < TC_USD_CUTOFF_HOUR ? TIPO_CAMBIO_USD_PREV : TIPO_CAMBIO_USD;
  }

  if (typeof target === 'string') {
    const s = target.trim();
    if (!s) return TIPO_CAMBIO_USD;
    if (s.includes('T') && (s.endsWith('Z') || /[+-]\d{2}:?\d{2}$/.test(s))) {
      const dt = new Date(s);
      if (!isNaN(dt.getTime())) {
        return getTipoCambioUsdForDateTime(dt);
      }
    }
    if (/^\d{4}-\d{2}$/.test(s)) {
      return s < '2026-11' ? TIPO_CAMBIO_USD_PREV : TIPO_CAMBIO_USD;
    }
    const datePart = s.slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
      if (datePart < TC_USD_CUTOFF_DATE) return TIPO_CAMBIO_USD_PREV;
      if (datePart > TC_USD_CUTOFF_DATE) return TIPO_CAMBIO_USD;
      if (s.length > 10) {
        const embeddedHour = _extractHourFromTimeHint(s.slice(10));
        if (embeddedHour !== null) {
          return embeddedHour < TC_USD_CUTOFF_HOUR ? TIPO_CAMBIO_USD_PREV : TIPO_CAMBIO_USD;
        }
      }
      const hintedHour = _extractHourFromTimeHint(timeOrEvent);
      if (hintedHour !== null) {
        return hintedHour < TC_USD_CUTOFF_HOUR ? TIPO_CAMBIO_USD_PREV : TIPO_CAMBIO_USD;
      }
      return TIPO_CAMBIO_USD;
    }
  }

  return TIPO_CAMBIO_USD;
};

export const getTipoCambioForPayment = (pago, eventOrContext = null, fallbackDateStr = null) => {
  if (pago && Number(pago.tipoCambioUSD) > 0) return Number(pago.tipoCambioUSD);
  if (pago && Number(pago.tipo_cambio) > 0) return Number(pago.tipo_cambio);
  const pagoDate =
    pago?.fecha_pago ||
    pago?.created_at ||
    fallbackDateStr ||
    (eventOrContext && typeof eventOrContext === 'object'
      ? eventOrContext.eventDate ||
        eventOrContext.fecha ||
        eventOrContext.form_data?.fecha ||
        eventOrContext.start_time
      : null);
  const pagoTime = pago?.hora_pago || pago?.created_at || eventOrContext;
  return getTipoCambioUsdForDateTime(pagoDate, pagoTime);
};

export const MODALITY_KEYS = ['US', 'RM', 'TAC', 'RX', 'EE', 'Otros'];

export const cleanPrice = (val) => {
  if (typeof val === 'number') return val;
  if (!val) return 0;
  return parseFloat(String(val).replace(/[^0-9.-]+/g, '')) || 0;
};

const _modalityCategoriesWeakCache = new WeakMap();
const _modalitySharesWeakCache = new WeakMap();
const _classifyModalityCache = new Map();
const _explicitOtrosCache = new Map();

export const classifyModalityString = (str) => {
  if (!str || typeof str !== 'string') return 'Otros';
  const cached = _classifyModalityCache.get(str);
  if (cached !== undefined) return cached;

  const s = str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .trim();
  if (!s) {
    _classifyModalityCache.set(str, 'Otros');
    return 'Otros';
  }

  let result = 'Otros';

  if (
    /\bUSB\b/.test(s) ||
    s === 'PASE' ||
    s === 'TRAD' ||
    s === 'PLACAS' ||
    s === 'OTROS' ||
    s === 'OTRO' ||
    s.includes('MEMORIA USB') ||
    s.includes('VENTA USB') ||
    s.includes('VENTA DE MEMORIA USB') ||
    s.includes('OTRO INGRESO') ||
    s.includes('PASE DE CORTESIA') ||
    s.includes('TRADUCCION')
  ) {
    result = 'Otros';
  } else if (
    s.includes('ANGIOTAC') ||
    s.includes('ANGIO TAC') ||
    s.includes('ANGIOTOMOGRAF') ||
    (s.includes('CORONARI') && (s.includes('TAC') || s.includes('TOMO') || s.includes('ANGIO')))
  ) {
    result = 'TAC';
  } else if (
    /\b(?:RM|RMI|RMN|MR|MRI)\b/.test(s) ||
    s.includes('RESONANCIA') ||
    s.includes('MAGNETIC') ||
    s.includes('COLANGIORESONANCIA') ||
    s.includes('ANGIORESONANCIA')
  ) {
    result = 'RM';
  } else if (
    /\b(?:TAC|TC|CT)\b/.test(s) ||
    s.includes('TOMOGRAF') ||
    s.includes('UROTOMOGRAF') ||
    s.includes('SCORE DE CALCIO') ||
    s.includes('SCORE CALCIO') ||
    s.includes('CALCIO SCORE')
  ) {
    result = 'TAC';
  } else if (
    /\b(?:US|USG|ECO)\b/.test(s) ||
    s.includes('ULTRASON') ||
    s.includes('ECOGRAF') ||
    s.includes('ECOCARDIO') ||
    s.includes('DOPPLER') ||
    s.includes('ELASTOGRAF') ||
    s.includes('ELASTOSONOGRAF') ||
    s.includes('TAMIZ DE CADERA') ||
    s.includes('TAMIZ CADERA') ||
    s.includes('PRUEBA DE RESERVA FETAL') ||
    s.includes('TESTICULAR E INGUINAL')
  ) {
    result = 'US';
  } else if (
    /\b(?:EE|EE_OTROS|MG|DMO|EKG|ECG|EEG|ELEC|SEGD)\b/.test(s) ||
    (!s.includes('RADIOGRAFIAS Y ESTUDIOS ESPECIALES') && s.includes('ESTUDIOS ESPECIALES')) ||
    s.includes('ESTUDIO ESPECIAL') ||
    s.includes('ELECTRO') ||
    s.includes('MASTO') ||
    s.includes('MAMOGRAF') ||
    s.includes('MAMOGRAMA') ||
    s.includes('DENSITO') ||
    s.includes('COLPO') ||
    s.includes('HISTEROSALPINGO') ||
    s.includes('HISTEROSALINGO') ||
    s.includes('HISTEROSONO') ||
    s.includes('HISTERO') ||
    s.includes('SONOHISTERO') ||
    s.includes('HIDROSONO') ||
    s.includes('ESOFAGOGRAMA') ||
    s.includes('SERIE ESOFAGO') ||
    s.includes('COLON POR ENEMA') ||
    s.includes('CISTOGRAMA') ||
    s.includes('URETROCISTOGRAMA') ||
    s.includes('UROGRAFIA') ||
    s.includes('FLUOROSCOP') ||
    s.includes('BIOPSIA') ||
    s.includes('HOLTER') ||
    s.includes('ESPIROMETR')
  ) {
    result = 'EE';
  } else if (
    /\b(?:RX|DX|CR)\b/.test(s) ||
    s.includes('RAYOS') ||
    s.includes('X-RAY') ||
    s.includes('RADIOGRAF') ||
    s.includes('RADIOLOG') ||
    s.includes('TELE DE TORAX') ||
    s.includes('TELE TORAX') ||
    s.includes('PERFILOGRAMA') ||
    s.includes('PLACAS AP') ||
    s.includes('PLACAS LAT') ||
    s.includes('PLACAS DINAMICAS') ||
    s.includes('ESCANOMETRIA') ||
    s.includes('ORTOPANTOMOGRAF') ||
    s.includes('PANORAMICA') ||
    s.includes('CEFALOMETR')
  ) {
    result = 'RX';
  }

  if (_classifyModalityCache.size > 4096) _classifyModalityCache.clear();
  _classifyModalityCache.set(str, result);
  return result;
};

const isExplicitOtrosOrLabString = (val) => {
  if (!val) return false;
  const key = typeof val === 'string' ? val : String(val);
  const cached = _explicitOtrosCache.get(key);
  if (cached !== undefined) return cached;

  const s = key
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .trim();
  const res = Boolean(
    s && (
      s === 'LAB' ||
      s === 'LABS' ||
      s.includes('LABORATORIO') ||
      s.includes('BIOMETRIA') ||
      s.includes('QUIMICA SANGUINEA') ||
      s.includes('UROCULTIVO') ||
      s.includes('EXAMEN GENERAL DE ORINA') ||
      s.includes('CHECK UP') ||
      s.includes('CHECKUP') ||
      /\bUSB\b/.test(s) ||
      s === 'PASE' ||
      s === 'TRAD' ||
      s.includes('TRADUCCION') ||
      s.includes('INTERPRETACION') ||
      s.includes('OTRO INGRESO') ||
      s.includes('SERVICIO ADMINISTRATIVO') ||
      s === 'CONTRASTE'
    )
  );
  if (_explicitOtrosCache.size > 4096) _explicitOtrosCache.clear();
  _explicitOtrosCache.set(key, res);
  return res;
};

export const resolveStudyModalityCategory = (est, fallbackModality = '') => {
  if (!est) return classifyModalityString(fallbackModality);
  if (typeof est === 'string') {
    const c = classifyModalityString(est);
    if (c !== 'Otros') return c;
    if (isExplicitOtrosOrLabString(est)) return 'Otros';
    return classifyModalityString(fallbackModality);
  }
  const rawMod = est.modalidad || est.modality || '';
  const rawName = est.nombre || est.estudio || est.descripcion_estudio || '';
  const nameCat = classifyModalityString(rawName);
  const modCat = classifyModalityString(rawMod);

  // If the study name explicitly identifies an Estudio Especial (e.g. Histerosalpingografía on DX detector)
  // or Angiotac Coronaria, honor the specific clinical category.
  if (nameCat === 'EE' && (modCat === 'RX' || modCat === 'Otros')) {
    return 'EE';
  }
  if (nameCat === 'TAC' && modCat === 'EE') {
    return 'TAC';
  }
  if (modCat !== 'Otros') {
    return modCat;
  }
  if (nameCat !== 'Otros') {
    return nameCat;
  }
  if (isExplicitOtrosOrLabString(rawMod) || isExplicitOtrosOrLabString(rawName)) {
    return 'Otros';
  }
  return classifyModalityString(fallbackModality);
};

export const getEventModalityCategories = (e) => {
  if (!e || typeof e !== 'object') return new Set(['Otros']);

  const fd = e.form_data || e.formData;
  const ext = e.extracted_data || e.extractedData;
  const cached = _modalityCategoriesWeakCache.get(e);
  if (
    cached &&
    cached.fd === fd &&
    cached.ext === ext &&
    cached.modality === e.modality &&
    cached.title === e.title
  ) {
    return cached.result;
  }

  const categories = new Set();

  if (fd?.estudios && Array.isArray(fd.estudios) && fd.estudios.length > 0) {
    fd.estudios.forEach(est => {
      categories.add(resolveStudyModalityCategory(est, e.modality));
    });
  } else if (ext?.estudios && Array.isArray(ext.estudios) && ext.estudios.length > 0) {
    ext.estudios.forEach(est => {
      categories.add(resolveStudyModalityCategory(est, e.modality));
    });
  } else if (e.modality) {
    const modCat = classifyModalityString(e.modality);
    if (modCat !== 'Otros') {
      categories.add(modCat);
    } else if (e.title || e.summary) {
      categories.add(classifyModalityString(e.title || e.summary));
    } else {
      categories.add('Otros');
    }
  } else if (e.title || e.summary) {
    categories.add(classifyModalityString(e.title || e.summary));
  } else {
    categories.add('Otros');
  }

  if (categories.size === 0) {
    categories.add('Otros');
  }

  _modalityCategoriesWeakCache.set(e, {
    fd,
    ext,
    modality: e.modality,
    title: e.title,
    result: categories
  });

  return categories;
};

/**
 * Computes normalized proportional revenue weights across canonical modality categories
 * ('US', 'RM', 'TAC', 'RX', 'EE', 'Otros') for an appointment event.
 *
 * Guarantees that the returned weights always sum to 1.0 even when:
 * - form_data.estudios is an empty array [] and extracted_data.estudios has items
 * - individual study prices are 0 while appointment cotizado/precio_total > 0
 * - study prices are formatted currency strings (e.g. "$1,500")
 * - an appointment combines clinical imaging with a LAB or USB study
 */
export const getEventModalityShares = (e) => {
  if (!e || typeof e !== 'object') {
    return [{ modCat: 'Otros', weight: 1 }];
  }
  const fd = e.form_data || e.formData;
  const ext = e.extracted_data || e.extractedData;
  const cached = _modalitySharesWeakCache.get(e);
  if (
    cached &&
    cached.fd === fd &&
    cached.ext === ext &&
    cached.modality === e.modality &&
    cached.title === e.title &&
    cached.summary === e.summary
  ) {
    return cached.result;
  }

  const fdEst = Array.isArray(fd?.estudios) && fd.estudios.length > 0 ? fd.estudios : null;
  const extEst = Array.isArray(ext?.estudios) && ext.estudios.length > 0 ? ext.estudios : null;
  const estList = fdEst || extEst || [];

  const weights = {};
  if (estList.length > 0) {
    const prices = estList.map((est) =>
      typeof est === 'object' && est !== null ? Math.max(0, cleanPrice(est.precio)) : 0
    );
    const totalEstudiosPrecio = prices.reduce((sum, p) => sum + p, 0);
    const fallbackMod = e.modality || fd?.modality || ext?.modalidad || e.title || e.summary || '';
    estList.forEach((est, idx) => {
      const w = totalEstudiosPrecio > 0 ? prices[idx] / totalEstudiosPrecio : 1 / estList.length;
      const cat = resolveStudyModalityCategory(est, fallbackMod);
      const safeCat = MODALITY_KEYS.includes(cat) ? cat : 'Otros';
      weights[safeCat] = (weights[safeCat] || 0) + w;
    });
  } else {
    const cats = Array.from(getEventModalityCategories(e));
    const w = 1 / (cats.length || 1);
    cats.forEach((cat) => {
      const safeCat = MODALITY_KEYS.includes(cat) ? cat : 'Otros';
      weights[safeCat] = (weights[safeCat] || 0) + w;
    });
  }

  const result = Object.entries(weights).map(([modCat, weight]) => ({ modCat, weight }));
  _modalitySharesWeakCache.set(e, {
    fd,
    ext,
    modality: e.modality,
    title: e.title,
    summary: e.summary,
    result
  });
  return result;
};

export const calculateMonthlyProjections = ({
  events = [],
  historyEvents = [],
  uniqueEvents = null,
  selectedMonth,
  selectedPreset,
  selectedModalities = ['ALL'],
  referenceDate = new Date()
}) => {
  if (selectedPreset && selectedPreset !== 'mtd') return null;
  const d = referenceDate;
  const pad = n => String(n).padStart(2, '0');
  const currentMonthStr = `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
  const effectiveMonth = selectedPreset === 'mtd' ? currentMonthStr : selectedMonth;

  if (effectiveMonth !== currentMonthStr) return null;

  const totalDaysInMonth = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  const currentDay = d.getDate();
  const remainingDays = totalDaysInMonth - currentDay;
  if (remainingDays <= 0) return null;

  let remainingSundays = 0;
  let remainingWeekdays = 0;
  for (let i = currentDay + 1; i <= totalDaysInMonth; i++) {
    const date = new Date(d.getFullYear(), d.getMonth(), i);
    if (date.getDay() === 0) remainingSundays++;
    else remainingWeekdays++;
  }

  const dailyHist = {};
  for (let i = 0; i < 30; i++) {
    const pastD = new Date(d.getFullYear(), d.getMonth(), d.getDate() - i - 1);
    const key = `${pastD.getFullYear()}-${pad(pastD.getMonth() + 1)}-${pad(pastD.getDate())}`;
    dailyHist[key] = {
      dateKey: key,
      isSunday: pastD.getDay() === 0,
      daysAgo: i + 1,
      ingresos: 0,
      pacientes: 0,
      estudios: 0
    };
  }

  // Combine and deduplicate events across current month and historical months
  let allUniqueEvents;
  if (Array.isArray(uniqueEvents)) {
    allUniqueEvents = uniqueEvents;
  } else {
    const allCombined = [...(events || []), ...(historyEvents || [])];
    const seenIds = new Set();
    allUniqueEvents = [];
    for (const e of allCombined) {
      if (!e) continue;
      const id = e.id || e._id;
      if (id) {
        if (seenIds.has(id)) continue;
        seenIds.add(id);
      }
      allUniqueEvents.push(e);
    }
  }

  const activeEvents = allUniqueEvents.filter(e => {
    if (!e || !e.time) return false;
    if (!selectedModalities || selectedModalities.includes('ALL') || selectedModalities.length === 0) return true;
    const cats = getEventModalityCategories(e);
    return selectedModalities.some(mod => cats.has(mod));
  });

  activeEvents.forEach(e => {
    if (!e.time) return;
    const eventDateKey = e.time.split('T')[0];

    // Patient and study count attributed to appointment date
    if (!e.isGhost && dailyHist[eventDateKey]) {
      dailyHist[eventDateKey].pacientes += 1;
      dailyHist[eventDateKey].estudios += (e.form_data?.estudios?.length || e.extracted_data?.estudios?.length || 1);
    }

    // Revenue attribution matching dailyData / corte_del_dia
    let hasConvenio = false;
    let cotizado = 0;
    if (e.form_data?.estudios && Array.isArray(e.form_data.estudios)) {
      cotizado = e.form_data.estudios.reduce((s, est) => s + (parseFloat(est.precio) || 0), 0);
    } else if (e.extracted_data?.estudios && Array.isArray(e.extracted_data.estudios)) {
      cotizado = e.extracted_data.estudios.reduce((s, est) => s + (parseFloat(est?.precio) || 0), 0);
    }
    if (cotizado === 0) {
      cotizado = parseFloat(e.extracted_data?.precio_total) || parseFloat(e.extracted_data?.presupuesto) || 0;
    }

    if (e.form_data && e.form_data.pagos && Array.isArray(e.form_data.pagos)) {
      const hasRealPagos = e.form_data.pagos.some(p => cleanPrice(p.monto) > 0 || (p.metodo || '').toUpperCase().includes('CORTES') || (p.metodo || '').toUpperCase().includes('CONVENIO'));
      let sumaRegulares = 0;
      if (hasRealPagos) {
        sumaRegulares = e.form_data.pagos.reduce((acc, p) => {
          const met = (p.metodo || '').toUpperCase();
          if (met.includes('CORTES') || met.includes('CONVENIO') || p.isReembolso || p.isPagoALab) return acc;
          const m = cleanPrice(p.monto);
          const isUSD = p.moneda === 'USD';
          const tcPago = getTipoCambioForPayment(p, e, eventDateKey);
          return acc + (isUSD ? m * tcPago : m);
        }, 0);
      }

      e.form_data.pagos.forEach(p => {
        const pagoDateKey = p.fecha_pago || eventDateKey;
        if (!pagoDateKey || !dailyHist[pagoDateKey]) return;

        const monto = cleanPrice(p.monto);
        const met = (p.metodo || '').toUpperCase();
        const tcPago = getTipoCambioForPayment(p, e, pagoDateKey);

        if (monto > 0 || met.includes('CORTES') || met.includes('CONVENIO') || p.isReembolso || p.isPagoALab) {
          let montoFinal = monto;
          if (p.isReembolso || p.isPagoALab) {
            montoFinal = -Math.abs(monto);
          } else if (met.includes('CORTES') || met.includes('CONVENIO')) {
            const isUSD = p.moneda === 'USD';
            const remanenteMXN = Math.max(0, cotizado - sumaRegulares);
            montoFinal = isUSD ? (remanenteMXN / tcPago) : remanenteMXN;
            if (met.includes('CONVENIO')) hasConvenio = true;
          }

          let montoMXN = p.moneda === 'USD' ? montoFinal * tcPago : montoFinal;

          if (!met.includes('CORTES')) {
            dailyHist[pagoDateKey].ingresos += montoMXN;
          }
        }
      });
    }

    // Convenio fallback
    const isConvenioEvent = (e.form_data && Array.isArray(e.form_data.pagos) && e.form_data.pagos.some(p => (p.metodo || '').toUpperCase().includes('CONVENIO'))) || (e.form_data && (e.form_data.metodo_pago || '').toUpperCase().includes('CONVENIO'));
    if (!hasConvenio && isConvenioEvent && eventDateKey && dailyHist[eventDateKey]) {
      dailyHist[eventDateKey].ingresos += cotizado;
    }
  });

  const monthStartKey = `${effectiveMonth}-01`;
  const hasHistoryLoaded = Boolean(historyEvents && historyEvents.length > 0);

  const calcProj = (field) => {
    let recentWeekdaySum = 0;
    let recentWeekdayCount = 0;
    let olderWeekdaySum = 0;
    let olderWeekdayCount = 0;

    let recentSundaySum = 0;
    let recentSundayCount = 0;
    let olderSundaySum = 0;
    let olderSundayCount = 0;

    Object.values(dailyHist).forEach(row => {
      // If day is before current month and history hasn't loaded yet, skip to avoid 0-pollution
      const isAvailable = row.dateKey >= monthStartKey || hasHistoryLoaded;
      if (!isAvailable) return;

      if (row.isSunday) {
        if (row.daysAgo <= 15) {
          recentSundaySum += row[field];
          recentSundayCount++;
        } else {
          olderSundaySum += row[field];
          olderSundayCount++;
        }
      } else {
        if (row.daysAgo <= 15) {
          recentWeekdaySum += row[field];
          recentWeekdayCount++;
        } else {
          olderWeekdaySum += row[field];
          olderWeekdayCount++;
        }
      }
    });

    const avgRecentWeekday = recentWeekdayCount > 0 ? recentWeekdaySum / recentWeekdayCount : 0;
    const avgOlderWeekday = olderWeekdayCount > 0 ? olderWeekdaySum / olderWeekdayCount : 0;

    let avgWeekday = 0;
    if (recentWeekdayCount > 0 && olderWeekdayCount > 0) {
      avgWeekday = (0.7 * avgRecentWeekday) + (0.3 * avgOlderWeekday);
    } else if (recentWeekdayCount > 0) {
      avgWeekday = avgRecentWeekday;
    } else if (olderWeekdayCount > 0) {
      avgWeekday = avgOlderWeekday;
    }

    const avgRecentSunday = recentSundayCount > 0 ? recentSundaySum / recentSundayCount : 0;
    const avgOlderSunday = olderSundayCount > 0 ? olderSundaySum / olderSundayCount : 0;

    let avgSunday = 0;
    if (recentSundayCount > 0 && olderSundayCount > 0) {
      avgSunday = (0.7 * avgRecentSunday) + (0.3 * avgOlderSunday);
    } else if (recentSundayCount > 0) {
      avgSunday = avgRecentSunday;
    } else if (olderSundayCount > 0) {
      avgSunday = avgOlderSunday;
    } else if (avgWeekday > 0) {
      avgSunday = avgWeekday * 0.35;
    }

    return (avgWeekday * remainingWeekdays) + (avgSunday * remainingSundays);
  };

  return {
    ingresos: calcProj('ingresos'),
    pacientes: Math.round(calcProj('pacientes')),
    estudios: Math.round(calcProj('estudios'))
  };
};

// Typical medical diagnostic seasonal baseline adjustments by calendar month (1..12) when YoY data is sparse
const DEFAULT_MONTH_SEASONALITY = {
  1: 0.96,  // Enero: arranque post-festivo
  2: 0.95,  // Febrero: mes corto
  3: 1.03,  // Marzo
  4: 0.97,  // Abril: Semana Santa
  5: 1.02,  // Mayo
  6: 1.01,  // Junio
  7: 0.98,  // Julio: verano
  8: 1.00,  // Agosto
  9: 0.99,  // Septiembre
  10: 1.04, // Octubre: mes fuerte campaña preventiva
  11: 0.97, // Noviembre: puentes / fin de año
  12: 0.87  // Diciembre: caída estacional fiestas decembrinas
};

const getEffectiveWorkingDays = (year, monthZeroIndexed) => {
  const daysInMonth = new Date(year, monthZeroIndexed + 1, 0).getDate();
  let weekdays = 0;
  let sundays = 0;
  for (let d = 1; d <= daysInMonth; d++) {
    const dow = new Date(year, monthZeroIndexed, d).getDay();
    if (dow === 0) sundays++;
    else weekdays++;
  }
  return weekdays + sundays * 0.35;
};

/**
 * Builds the 9-month historical + current month + remaining Quarter months projection series.
 * - Shows 9 months up to the reference/current month.
 * - Completes the current month bar with `proyeccion` (remaining projected revenue) and connects
 *   `pacientesProy` (dashed lighter green line) from the previous month to the current month's projected total.
 * - Appends the remaining months of the current Quarter (e.g., Nov and Dec when in Oct),
 *   projected using the last 3 months' values/growth and corrected by historical YoY seasonal factors
 *   for those same months in prior years.
 */
export const buildNineMonthAndQuarterComparison = ({
  monthlyBuckets = {},
  currentMonthKey,
  currentMonthProjection = null,
  referenceDate = new Date(),
  monthsCount = 12
}) => {
  const pad = n => String(n).padStart(2, '0');
  const monthNamesShort = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

  const refYear = currentMonthKey
    ? parseInt(currentMonthKey.split('-')[0], 10)
    : referenceDate.getFullYear();
  const refMonthZero = currentMonthKey
    ? parseInt(currentMonthKey.split('-')[1], 10) - 1
    : referenceDate.getMonth();

  const actualCurrentKey = `${referenceDate.getFullYear()}-${pad(referenceDate.getMonth() + 1)}`;

  // 1. Build the 12 months ending at (refYear, refMonthZero)
  const nineMonthKeys = [];
  const totalMonthsToBuild = Math.max(1, Number(monthsCount) || 12);
  for (let i = totalMonthsToBuild - 1; i >= 0; i--) {
    const d = new Date(refYear, refMonthZero - i, 1);
    const k = `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
    nineMonthKeys.push({
      key: k,
      year: d.getFullYear(),
      monthZero: d.getMonth(),
      name: `${monthNamesShort[d.getMonth()]} ${String(d.getFullYear()).slice(2)}`,
      isCurrentMonth: k === actualCurrentKey,
      isFutureQuarter: false
    });
  }

  // 2. Determine remaining months of the Quarter for the last month in nineMonthKeys
  const quarterEndMonthZero = Math.floor(refMonthZero / 3) * 3 + 2;
  const futureQuarterMonths = [];
  if ( `${refYear}-${pad(refMonthZero + 1)}` === actualCurrentKey ) {
    for (let m = refMonthZero + 1; m <= quarterEndMonthZero; m++) {
      const d = new Date(refYear, m, 1);
      const k = `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
      futureQuarterMonths.push({
        key: k,
        year: d.getFullYear(),
        monthZero: d.getMonth(),
        name: `${monthNamesShort[d.getMonth()]} ${String(d.getFullYear()).slice(2)}`,
        isCurrentMonth: false,
        isFutureQuarter: true
      });
    }
  }

  const getBucket = (k) => {
    const b = monthlyBuckets[k] || {};
    const US = b.US || 0;
    const RM = b.RM || 0;
    const TAC = b.TAC || 0;
    const RX = b.RX || 0;
    const EE = b.EE || 0;
    const Otros = b.Otros || 0;
    const total = b.total !== undefined ? b.total : (US + RM + TAC + RX + EE + Otros);
    const pacientes = b.pacientes || 0;
    return { US, RM, TAC, RX, EE, Otros, total, pacientes };
  };

  // Assemble the 9 historical/current entries first
  const series = nineMonthKeys.map((mInfo) => {
    const b = getBucket(mInfo.key);
    const isCurr = mInfo.isCurrentMonth && currentMonthProjection;
    const projRemainingIngresos = isCurr ? Math.max(0, Math.round(currentMonthProjection.ingresos || 0)) : 0;
    const projRemainingPacientes = isCurr ? Math.max(0, Math.round(currentMonthProjection.pacientes || 0)) : 0;
    const fullProjectedIngresos = Math.round(b.total + projRemainingIngresos);
    const fullProjectedPacientes = Math.round(b.pacientes + projRemainingPacientes);

    return {
      key: mInfo.key,
      year: mInfo.year,
      monthZero: mInfo.monthZero,
      name: mInfo.name,
      US: Math.round(b.US),
      RM: Math.round(b.RM),
      TAC: Math.round(b.TAC),
      RX: Math.round(b.RX),
      EE: Math.round(b.EE),
      Otros: Math.round(b.Otros),
      total: Math.round(b.total),
      proyeccion: projRemainingIngresos,
      projectedTotal: isCurr ? fullProjectedIngresos : Math.round(b.total),
      pacientes: isCurr ? null : b.pacientes,
      pacientesActual: b.pacientes,
      pacientesProy: isCurr ? fullProjectedPacientes : null,
      isCurrentMonth: Boolean(mInfo.isCurrentMonth),
      isFutureQuarter: false
    };
  });

  // Connect the dashed projection line from the month immediately before the current month
  const currIdx = series.findIndex(s => s.isCurrentMonth && s.proyeccion > 0);
  if (currIdx > 0) {
    series[currIdx - 1].pacientesProy = series[currIdx - 1].pacientesActual;
  }

  // 3. Project remaining months of the Quarter using last 3 months + YoY seasonal correction
  if (futureQuarterMonths.length > 0 && series.length >= 3) {
    const m0 = series[series.length - 1]; // Current month (with full projection)
    const m1 = series[series.length - 2]; // Prev month (-1)
    const m2 = series[series.length - 3]; // Prev month (-2)

    const rev0 = m0.projectedTotal || m0.total || 0;
    const rev1 = m1.projectedTotal || m1.total || 0;
    const rev2 = m2.projectedTotal || m2.total || 0;

    const pax0 = m0.pacientesProy || m0.pacientesActual || 0;
    const pax1 = m1.pacientesActual || 0;
    const pax2 = m2.pacientesActual || 0;

    // Weighted 3-month baseline (50% M0, 30% M-1, 20% M-2)
    const calcWeighted3M = (v0, v1, v2) => {
      const items = [
        { v: v0, w: 0.50 },
        { v: v1, w: 0.30 },
        { v: v2, w: 0.20 }
      ].filter(x => x.v > 0);
      if (items.length === 0) return 0;
      const totalW = items.reduce((s, x) => s + x.w, 0);
      return items.reduce((s, x) => s + (x.v * x.w), 0) / totalW;
    };

    // Average MoM growth rate across the last 3 months (damped so noise doesn't explode)
    const calcTrendFactor = (v0, v1, v2) => {
      const ratios = [];
      if (v2 > 0 && v1 > 0) ratios.push(Math.max(0.75, Math.min(1.30, v1 / v2)));
      if (v1 > 0 && v0 > 0) ratios.push(Math.max(0.75, Math.min(1.30, v0 / v1)));
      if (ratios.length === 0) return 1.0;
      const avgRatio = ratios.reduce((a, b) => a + b, 0) / ratios.length;
      return 1 + 0.45 * (avgRatio - 1);
    };

    const baseRev3M = calcWeighted3M(rev0, rev1, rev2);
    const basePax3M = calcWeighted3M(pax0, pax1, pax2);
    const revTrend = calcTrendFactor(rev0, rev1, rev2);
    const paxTrend = calcTrendFactor(pax0, pax1, pax2);

    // Ensure previous point has pacientesProy set so the dashed line is continuous
    if (m0.pacientesProy === null || m0.pacientesProy === undefined) {
      m0.pacientesProy = m0.pacientesActual;
      if (series.length >= 2 && series[series.length - 2].pacientesProy === null) {
        series[series.length - 2].pacientesProy = series[series.length - 2].pacientesActual;
      }
    }

    const baseWorkDays = getEffectiveWorkingDays(refYear, refMonthZero);
    const baseDefaultSeason = DEFAULT_MONTH_SEASONALITY[refMonthZero + 1] || 1.0;

    futureQuarterMonths.forEach((fm, stepIdx) => {
      const stepNumber = stepIdx + 1;
      const targetMonthNum = fm.monthZero + 1;

      // Check historical YoY data (same month in year-1 and year-2 vs refMonth in year-1 and year-2)
      const yoyRevRatios = [];
      const yoyPaxRatios = [];
      for (let yrOffset = 1; yrOffset <= 2; yrOffset++) {
        const histYear = fm.year - yrOffset;
        const histTargetKey = `${histYear}-${pad(targetMonthNum)}`;
        const histRefKey = `${histYear}-${pad(refMonthZero + 1)}`;
        const histPrevKey = `${histYear}-${pad(refMonthZero)}`;

        const bTarget = getBucket(histTargetKey);
        const bRef = getBucket(histRefKey);
        const bPrev = getBucket(histPrevKey);

        const refAnchorRev = (bRef.total > 0 && bPrev.total > 0)
          ? (0.65 * bRef.total + 0.35 * bPrev.total)
          : (bRef.total || bPrev.total || 0);
        if (bTarget.total > 0 && refAnchorRev > 0) {
          yoyRevRatios.push(Math.max(0.55, Math.min(1.45, bTarget.total / refAnchorRev)));
        }

        const refAnchorPax = (bRef.pacientes > 0 && bPrev.pacientes > 0)
          ? (0.65 * bRef.pacientes + 0.35 * bPrev.pacientes)
          : (bRef.pacientes || bPrev.pacientes || 0);
        if (bTarget.pacientes > 0 && refAnchorPax > 0) {
          yoyPaxRatios.push(Math.max(0.55, Math.min(1.45, bTarget.pacientes / refAnchorPax)));
        }
      }

      // Calendar & seasonal prior
      const targetWorkDays = getEffectiveWorkingDays(fm.year, fm.monthZero);
      const workDayRatio = baseWorkDays > 0 ? (targetWorkDays / baseWorkDays) : 1.0;
      const defaultSeasonRatio = (DEFAULT_MONTH_SEASONALITY[targetMonthNum] || 1.0) / baseDefaultSeason;
      const priorSeasonalFactor = 0.5 * workDayRatio + 0.5 * defaultSeasonRatio;

      const empiricalRevSeason = yoyRevRatios.length > 0
        ? (yoyRevRatios.reduce((a, b) => a + b, 0) / yoyRevRatios.length)
        : null;
      const empiricalPaxSeason = yoyPaxRatios.length > 0
        ? (yoyPaxRatios.reduce((a, b) => a + b, 0) / yoyPaxRatios.length)
        : null;

      const finalRevSeasonFactor = empiricalRevSeason !== null
        ? (0.70 * empiricalRevSeason + 0.30 * priorSeasonalFactor)
        : priorSeasonalFactor;
      const finalPaxSeasonFactor = empiricalPaxSeason !== null
        ? (0.70 * empiricalPaxSeason + 0.30 * priorSeasonalFactor)
        : priorSeasonalFactor;

      const compoundedRevTrend = Math.pow(revTrend, stepNumber);
      const compoundedPaxTrend = Math.pow(paxTrend, stepNumber);

      const projectedRev = Math.max(0, Math.round(baseRev3M * compoundedRevTrend * finalRevSeasonFactor));
      const projectedPax = Math.max(0, Math.round(basePax3M * compoundedPaxTrend * finalPaxSeasonFactor));

      series.push({
        key: fm.key,
        year: fm.year,
        monthZero: fm.monthZero,
        name: `${fm.name} (Proy.)`,
        shortName: fm.name,
        US: 0,
        RM: 0,
        TAC: 0,
        RX: 0,
        EE: 0,
        Otros: 0,
        total: 0,
        proyeccion: projectedRev,
        projectedTotal: projectedRev,
        pacientes: null,
        pacientesActual: 0,
        pacientesProy: projectedPax,
        isCurrentMonth: false,
        isFutureQuarter: true,
        projectionMeta: {
          baseRev3M: Math.round(baseRev3M),
          trendPct: Math.round((compoundedRevTrend - 1) * 1000) / 10,
          seasonalFactorPct: Math.round((finalRevSeasonFactor - 1) * 1000) / 10,
          usedHistoricalYoY: empiricalRevSeason !== null
        }
      });
    });
  }

  return series;
};

const tijuanaTimeFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/Tijuana',
  hour: 'numeric',
  minute: 'numeric',
  second: 'numeric',
  hour12: false
});

const _tsMinsCache = new Map();
const _utcHourToTjHourCache = new Map();
const _finalizedTimesWeakCache = new WeakMap();

export const parseTimestampToTijuanaMins = (val, { assumeNaiveIsoUtc = false } = {}) => {
  if (!val) return null;
  const s = String(val).trim();
  if (!s || s === '-') return null;

  const cacheKey = assumeNaiveIsoUtc ? `${s}|1` : s;
  const cachedVal = _tsMinsCache.get(cacheKey);
  if (cachedVal !== undefined) return cachedVal;

  const storeResult = (res) => {
    if (_tsMinsCache.size > 20000) _tsMinsCache.clear();
    _tsMinsCache.set(cacheKey, res);
    return res;
  };

  // 1. Time-only or locale date+time strings: "09:15", "09:15:30", "2:30 PM", "10/10/2026, 10:08:00"
  if (!s.includes('T') && !/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const mTime = s.match(/(\d{1,2}):(\d{2})(?::(\d{2}))?(?:\s*(a\.?\s*m\.?|p\.?\s*m\.?|am|pm))?/i);
    if (mTime) {
      let h = parseInt(mTime[1], 10);
      const m = parseInt(mTime[2], 10);
      const sec = mTime[3] ? parseInt(mTime[3], 10) : 0;
      const ampm = (mTime[4] || '').toLowerCase();
      if (!isNaN(h) && !isNaN(m)) {
        const isPM = ampm.includes('p');
        const isAM = ampm.includes('a');
        if (isPM && h < 12) h += 12;
        if (isAM && h === 12) h = 0;
        return storeResult(h * 60 + m + (isNaN(sec) ? 0 : sec / 60));
      }
    }
  }

  // 2. Full ISO or YYYY-MM-DD HH:MM:SS datetime strings
  let isoStr = s;
  if (/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}/.test(s)) {
    isoStr = s.replace(/\s+/, 'T');
  }

  // Fast-path for ISO strings already in America/Tijuana offset (-07:00 or -08:00)
  const mTjIso = isoStr.match(/T(\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?-0[78]:?00$/);
  if (mTjIso) {
    const h = parseInt(mTjIso[1], 10);
    const m = parseInt(mTjIso[2], 10);
    const sec = mTjIso[3] ? parseInt(mTjIso[3], 10) : 0;
    return storeResult(h * 60 + m + sec / 60);
  }

  const hasExplicitTz = isoStr.endsWith('Z') || /[+-]\d{2}:?\d{2}$/.test(isoStr);

  // Fast-path for UTC ISO strings (ending in Z, or naive ISO with assumeNaiveIsoUtc):
  // America/Tijuana UTC offsets are whole hours (-07:00 / -08:00) transitioning on the exact hour.
  if (isoStr.endsWith('Z') || (!hasExplicitTz && assumeNaiveIsoUtc)) {
    const mUtcIso = isoStr.match(/^(\d{4}-\d{2}-\d{2}T\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?Z?$/);
    if (mUtcIso) {
      const utcHourPrefix = mUtcIso[1];
      const m = parseInt(mUtcIso[2], 10);
      const sec = mUtcIso[3] ? parseInt(mUtcIso[3], 10) : 0;
      if (m < 60 && sec < 60) {
        let tjHour = _utcHourToTjHourCache.get(utcHourPrefix);
        if (tjHour === undefined) {
          const dHour = new Date(`${utcHourPrefix}:00:00Z`);
          if (!isNaN(dHour.getTime())) {
            const fParts = tijuanaTimeFormatter.formatToParts(dHour);
            tjHour = 0;
            for (const p of fParts) {
              if (p.type === 'hour') {
                tjHour = parseInt(p.value, 10) % 24;
                break;
              }
            }
            if (_utcHourToTjHourCache.size > 10000) _utcHourToTjHourCache.clear();
            _utcHourToTjHourCache.set(utcHourPrefix, tjHour);
          } else {
            tjHour = null;
          }
        }
        if (tjHour !== null && tjHour !== undefined) {
          return storeResult(tjHour * 60 + m + sec / 60);
        }
      }
    }
  }

  if (isoStr.includes('T') && !hasExplicitTz) {
    if (assumeNaiveIsoUtc) {
      const dUtc = new Date(`${isoStr}Z`);
      if (!isNaN(dUtc.getTime())) {
        const fParts = tijuanaTimeFormatter.formatToParts(dUtc);
        let h = 0, m = 0, sec = 0;
        for (const p of fParts) {
          if (p.type === 'hour') h = parseInt(p.value, 10) % 24;
          if (p.type === 'minute') m = parseInt(p.value, 10);
          if (p.type === 'second') sec = parseInt(p.value, 10);
        }
        return storeResult(h * 60 + m + sec / 60);
      }
    } else {
      const timePart = isoStr.split('T')[1] || '';
      const mNaive = timePart.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);
      if (mNaive) {
        const h = parseInt(mNaive[1], 10);
        const m = parseInt(mNaive[2], 10);
        const sec = mNaive[3] ? parseInt(mNaive[3], 10) : 0;
        return storeResult(h * 60 + m + (isNaN(sec) ? 0 : sec / 60));
      }
    }
  }

  const d = new Date(isoStr);
  if (!isNaN(d.getTime())) {
    const fParts = tijuanaTimeFormatter.formatToParts(d);
    let h = 0, m = 0, sec = 0;
    for (const p of fParts) {
      if (p.type === 'hour') h = parseInt(p.value, 10) % 24;
      if (p.type === 'minute') m = parseInt(p.value, 10);
      if (p.type === 'second') sec = parseInt(p.value, 10);
    }
    return storeResult(h * 60 + m + sec / 60);
  }

  return storeResult(null);
};

const _EMPTY_FINALIZED_TIMES = Object.freeze({
  isFinalizado: false,
  numEstudios: 0,
  retrasoLlegada: null,
  tiempoRegistro: null,
  tiempoEsperaTotal: null,
  tiempoEstudioTotal: null,
  tiempoEsperaPorEstudio: null,
  tiempoEstudioPorEstudio: null
});

/**
 * Extracts operational times for finalized study patients:
 * - Atrasos: max(0, tLlegada - tAgendado). If patient arrives early (tLlegada <= tAgendado),
 *   atraso is 0 and effective arrival time is tAgendado.
 * - Registro: time between effective arrival and admission (tAdmitido - effectiveLlegada).
 * - Espera: sum of wait intervals (admission -> study 1 start + any inter-study wait after partial finish).
 *   When computing averages across patients, divide sum of waits by number of studies (numEstudios).
 * - Estudio: sum of study durations across all studies of the patient.
 *   When computing averages across patients, divide sum of study durations by number of studies (numEstudios).
 */
export const extractFinalizedEventTimes = (e) => {
  if (!e || typeof e !== 'object' || e.isGhost) return _EMPTY_FINALIZED_TIMES;

  const cachedEntry = _finalizedTimesWeakCache.get(e);
  if (
    cachedEntry &&
    cachedEntry.estado === e.estado &&
    cachedEntry.fd === e.form_data &&
    cachedEntry.time === e.time &&
    cachedEntry.start_time === e.start_time &&
    cachedEntry.hora_ingreso === e.hora_ingreso
  ) {
    return cachedEntry.result;
  }

  const fd = e.form_data || {};
  const ext = e.extracted_data || e.extracted || {};
  const estUpper = String(e.estado || fd.estado || fd.status || e.status || '').toUpperCase().trim();
  const historial = Array.isArray(fd.historial)
    ? fd.historial
    : (Array.isArray(e.historial) ? e.historial : []);

  const hasFinalizadoInHist = historial.some(h => {
    const act = String(h?.action || h?.accion || h?.estado || '').toUpperCase();
    return (act.includes('FINALIZADO') || act.includes('EXITOSAMENTE') || act.includes('FIRMADO')) && !act.includes('PARCIALMENTE');
  });

  const isFinalizado =
    estUpper === 'FINALIZADO' ||
    estUpper === 'FIRMADO' ||
    estUpper === 'ATENDIDO' ||
    estUpper === 'ENTREGADO' ||
    hasFinalizadoInHist ||
    Boolean(fd.hora_salida || fd.hora_fin_estudio || fd.hora_finalizado || fd.hora_fin || e.hora_fin || fd.sala_estudio_end_time);

  if (!isFinalizado) {
    _finalizedTimesWeakCache.set(e, {
      estado: e.estado,
      fd: e.form_data,
      time: e.time,
      start_time: e.start_time,
      hora_ingreso: e.hora_ingreso,
      result: _EMPTY_FINALIZED_TIMES
    });
    return _EMPTY_FINALIZED_TIMES;
  }

  const estudiosArr = (Array.isArray(fd.estudios) && fd.estudios.length > 0)
    ? fd.estudios
    : (Array.isArray(ext.estudios) && ext.estudios.length > 0 ? ext.estudios : []);
  const numEstudios = Math.max(1, estudiosArr.length || 1);

  const isWalkIn =
    Boolean(fd.walk_in) ||
    String(e.title || e.summary || '').toUpperCase().includes('WALK-IN') ||
    String(e.paciente || '').toUpperCase().includes('WALK-IN');

  // If rescheduled inside historial, take the active post-reschedule segment (matching CorteDelDia)
  let lastRescheduleIdx = -1;
  for (let i = 0; i < historial.length; i++) {
    const act = String(historial[i]?.action || historial[i]?.accion || historial[i]?.estado || '').toUpperCase();
    const det = String(historial[i]?.details || historial[i]?.detalles || '').toUpperCase();
    if (act.includes('REPROGRAMAD') || act.includes('RE-PROGRAMAD') || act.includes('REAGENDAD') || det.includes('REPROGRAMAD')) {
      lastRescheduleIdx = i;
    }
  }
  const activeHist = lastRescheduleIdx !== -1 && lastRescheduleIdx + 1 < historial.length
    ? historial.slice(lastRescheduleIdx + 1)
    : historial;

  let tAgendado = parseTimestampToTijuanaMins(e.original_time || e.start_time || e.time || e.start || e.fecha_cita || fd.hora_cita || fd.hora_agendada || fd.hora || e.hora_agendada || e.hora);
  let tLlegada = parseTimestampToTijuanaMins(e.hora_ingreso || fd.hora_ingreso || fd.hora_llegada_iso || e.hora_llegada_iso || fd.hora_llegada || e.hora_llegada || fd.llegada);
  let tAdmitido = parseTimestampToTijuanaMins(fd.hora_admision || fd.hora_admitido_dt || fd.hora_registrado || fd.hora_admitido || e.hora_admitido || fd.admision);
  let tInicioFirst = parseTimestampToTijuanaMins(fd.hora_inicio_estudio || fd.hora_inicio || e.hora_inicio || fd.hora_paso_sala || fd.inicio_estudio || fd.sala_estudio_start_time);
  let tFinLast = parseTimestampToTijuanaMins(fd.hora_fin_estudio || fd.hora_fin || e.hora_fin || fd.hora_salida || fd.hora_finalizado || fd.fin_estudio || fd.sala_estudio_end_time);

  let waitStart = null;
  let studyStart = null;
  let inStudy = false;
  let sumWaitSegments = 0;
  let hasWaitSegments = false;
  let sumStudySegments = 0;
  let hasStudySegments = false;

  for (const h of activeHist) {
    if (!h || typeof h !== 'object') continue;
    const rawTs = h.timestamp || h.fecha;
    const t = parseTimestampToTijuanaMins(rawTs, { assumeNaiveIsoUtc: Boolean(h.timestamp) });
    if (t === null) continue;
    const act = String(h.action || h.accion || h.estado || '').toUpperCase();
    const det = String(h.details || h.detalles || '').toUpperCase();

    if (act.includes('INGRESÓ A RECEPCIÓN') || act.includes('INGRESO A RECEPCION') || act.includes('WALK-IN REGISTRADO') || det.includes('INGRESÓ A RECEPCIÓN') || act === 'EN_PROCESO' || act.includes('LLEGADA')) {
      tLlegada = t;
    }
    const isStudyRoomTransition =
      act.includes('EN_ESTUDIO') ||
      act.includes('EN ESTUDIO') ||
      act.includes('PACIENTE EN SALA DE ESTUDIO') ||
      act.includes('ESTUDIO PASADO A SALA') ||
      act.includes('INICIO ESTUDIO') ||
      act.includes('INICIÓ ESTUDIO');

    if (!isStudyRoomTransition && (act.includes('ADMITIDO') || act.includes('PASÓ A SALA') || act.includes('PASO A SALA') || det.includes('PASÓ A SALA DE ESPERA') || act.includes('REGISTRADO'))) {
      tAdmitido = t;
      if (!inStudy) {
        waitStart = t;
      }
    }
    if (isStudyRoomTransition) {
      if (tInicioFirst === null) tInicioFirst = t;
      if (inStudy && studyStart !== null) {
        const dStudy = t - studyStart;
        if (dStudy >= 0 && dStudy <= 360) {
          sumStudySegments += dStudy;
          hasStudySegments = true;
        }
      } else {
        const wRef = waitStart !== null ? waitStart : (tAdmitido !== null ? tAdmitido : tLlegada);
        if (wRef !== null) {
          const dWait = t - wRef;
          if (dWait >= 0 && dWait <= 360) {
            sumWaitSegments += dWait;
            hasWaitSegments = true;
          }
        }
      }
      studyStart = t;
      inStudy = true;
      waitStart = null;
    }
    if (act.includes('FINALIZÓ PARCIALMENTE') || act.includes('FINALIZO PARCIALMENTE')) {
      if (inStudy && studyStart !== null) {
        const dStudy = t - studyStart;
        if (dStudy >= 0 && dStudy <= 360) {
          sumStudySegments += dStudy;
          hasStudySegments = true;
        }
      }
      inStudy = false;
      studyStart = null;
      waitStart = t;
    }
    if ((act.includes('FINALIZADO') || act.includes('EXITOSAMENTE') || act.includes('FIRMADO')) && !act.includes('PARCIALMENTE')) {
      tFinLast = t;
      if (inStudy && studyStart !== null) {
        const dStudy = t - studyStart;
        if (dStudy >= 0 && dStudy <= 360) {
          sumStudySegments += dStudy;
          hasStudySegments = true;
        }
      }
      inStudy = false;
      studyStart = null;
      waitStart = null;
    }
  }

  // Fallback to per-study timestamps on estudiosArr if segments were not recorded in historial
  if ((!hasWaitSegments || !hasStudySegments) && estudiosArr.length > 0) {
    let estWaitSum = 0;
    let estWaitFound = false;
    let estStudySum = 0;
    let estStudyFound = false;

    for (const est of estudiosArr) {
      if (!est || typeof est !== 'object') continue;
      const tWait0 = parseTimestampToTijuanaMins(est.hora_inicio_espera || est.inicio_espera);
      const tSala = parseTimestampToTijuanaMins(est.hora_pase_sala || est.hora_inicio || est.inicio_estudio);
      const tEnd = parseTimestampToTijuanaMins(est.hora_finalizado || est.hora_fin || est.fin_estudio);

      if (tWait0 !== null && tSala !== null && tSala >= tWait0 && (tSala - tWait0) <= 360) {
        estWaitSum += (tSala - tWait0);
        estWaitFound = true;
      }
      if (tSala !== null && tEnd !== null && tEnd >= tSala && (tEnd - tSala) <= 360) {
        estStudySum += (tEnd - tSala);
        estStudyFound = true;
      }
    }

    if (!hasWaitSegments && estWaitFound) {
      sumWaitSegments = estWaitSum;
      hasWaitSegments = true;
    }
    if (!hasStudySegments && estStudyFound) {
      sumStudySegments = estStudySum;
      hasStudySegments = true;
    }
  }

  if (tAdmitido !== null && tLlegada === null) {
    tLlegada = tAdmitido;
  }

  // 1. Atrasos & Effective Arrival Time:
  // If patient arrives before scheduled appointment, arrival delay is 0 and effective arrival is scheduled time.
  let retrasoLlegada = null;
  let effectiveLlegada = tLlegada;
  if (tLlegada !== null) {
    if (isWalkIn || tAgendado === null) {
      retrasoLlegada = 0;
      effectiveLlegada = tLlegada;
    } else {
      const diffLlegada = tLlegada - tAgendado;
      if (diffLlegada > 0 && diffLlegada <= 360) {
        retrasoLlegada = diffLlegada;
        effectiveLlegada = tLlegada;
      } else if (diffLlegada <= 0 && diffLlegada >= -240) {
        retrasoLlegada = 0;
        effectiveLlegada = tAgendado;
      }
    }
  }

  // 2. Registro: time between arrival (effectiveLlegada, or tLlegada if admitted before scheduled time) and admission
  let tiempoRegistro = null;
  if (tAdmitido !== null && effectiveLlegada !== null) {
    if (tAdmitido >= effectiveLlegada) {
      const diffReg = tAdmitido - effectiveLlegada;
      if (diffReg >= 0 && diffReg <= 240) {
        tiempoRegistro = diffReg;
      }
    } else if (tLlegada !== null && tAdmitido >= tLlegada) {
      const diffReg = tAdmitido - tLlegada;
      if (diffReg >= 0 && diffReg <= 240) {
        tiempoRegistro = diffReg;
      }
    } else {
      tiempoRegistro = 0;
    }
  }

  // 3. Espera (Total across patient's studies)
  let tiempoEsperaTotal = null;
  if (hasWaitSegments) {
    tiempoEsperaTotal = sumWaitSegments;
  } else {
    const startWaitRef = tAdmitido !== null ? tAdmitido : effectiveLlegada;
    if (startWaitRef !== null && tInicioFirst !== null) {
      const diffWait = tInicioFirst - startWaitRef;
      if (diffWait >= 0 && diffWait <= 360) {
        tiempoEsperaTotal = diffWait;
      } else if (tLlegada !== null && tInicioFirst >= tLlegada && (tInicioFirst - tLlegada) <= 360) {
        tiempoEsperaTotal = tInicioFirst - tLlegada;
      }
    }
  }

  // 4. Estudio (Total across patient's studies)
  let tiempoEstudioTotal = null;
  if (hasStudySegments && sumStudySegments > 0) {
    tiempoEstudioTotal = sumStudySegments;
  } else if (tInicioFirst !== null && tFinLast !== null) {
    const diffStudy = tFinLast - tInicioFirst;
    if (diffStudy >= 0 && diffStudy <= 360) {
      tiempoEstudioTotal = diffStudy;
    }
  }

  const finalResult = {
    isFinalizado: true,
    numEstudios,
    retrasoLlegada,
    tiempoRegistro,
    tiempoEsperaTotal,
    tiempoEstudioTotal,
    tiempoEsperaPorEstudio: tiempoEsperaTotal !== null ? (tiempoEsperaTotal / numEstudios) : null,
    tiempoEstudioPorEstudio: tiempoEstudioTotal !== null ? (tiempoEstudioTotal / numEstudios) : null
  };

  _finalizedTimesWeakCache.set(e, {
    estado: e.estado,
    fd: e.form_data,
    time: e.time,
    start_time: e.start_time,
    hora_ingreso: e.hora_ingreso,
    result: finalResult
  });

  return finalResult;
};

// Shared module-level caches for VistaSemanal, VistaMensual, VistaAnual, and VistaMultianual to survive tab switches
const _SHARED_RANGE_EVENTS_CACHE = new Map();
const _DERIVED_RANGE_SLICE_CACHE = new Map();
const _INFLIGHT_RANGE_PROMISES = new Map();
const _SHARED_YEAR_EVENTS_CACHE = new Map();
const _INFLIGHT_YEAR_PROMISES = new Map();
let _SHARED_ALL_YEARS_EVENTS_CACHE = null;
let _INFLIGHT_ALL_YEARS_PROMISE = null;
let _analyticsCacheGeneration = 0;
const _SHARED_EVENTS_CACHE_TTL_MS = 1800 * 1000;

const shiftIsoDateByDays = (isoDateStr, deltaDays) => {
  const parts = String(isoDateStr || '').split('-').map(Number);
  if (parts.length !== 3 || parts.some(Number.isNaN)) return isoDateStr;
  const dt = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2] + deltaDays));
  const y = dt.getUTCFullYear();
  const m = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const d = String(dt.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

export const sliceEventsForDateRange = (sourceEvents, startDate, endDate) => {
  if (!Array.isArray(sourceEvents) || sourceEvents.length === 0 || !startDate || !endDate) {
    return null;
  }
  const prevDay = shiftIsoDateByDays(startDate, -1);
  const nextDay = shiftIsoDateByDays(endDate, 1);
  const lookbackStart = shiftIsoDateByDays(startDate, -90);
  const sliced = [];
  const seenIds = new Set();

  for (let i = 0; i < sourceEvents.length; i++) {
    const ev = sourceEvents[i];
    if (!ev || typeof ev !== 'object') continue;
    const rawId = ev.id || ev.google_event_id;
    const evDay = String(ev.time || ev.start_time || '').slice(0, 10);

    if (!ev.isGhost && evDay >= prevDay && evDay <= nextDay) {
      if (rawId) {
        const k = `p:${rawId}`;
        if (seenIds.has(k)) continue;
        seenIds.add(k);
      }
      sliced.push(ev);
      continue;
    }

    if (!ev.isGhost) {
      const hiDay = String(ev.hora_ingreso || ev.form_data?.hora_ingreso || '').slice(0, 10);
      const fAgDay = String(
        ev.form_data?.fecha_agendada || ev.form_data?.fecha || ev.form_data?.fecha_cita || ''
      ).slice(0, 10);
      if (
        (hiDay && hiDay >= startDate && hiDay <= endDate) ||
        (fAgDay && fAgDay >= startDate && fAgDay <= endDate)
      ) {
        if (rawId) {
          const k = `p:${rawId}`;
          if (seenIds.has(k)) continue;
          seenIds.add(k);
        }
        sliced.push(ev);
        continue;
      }
    }

    if (evDay && evDay >= lookbackStart && evDay < startDate) {
      const pagos = ev.form_data?.pagos;
      if (
        Array.isArray(pagos) &&
        pagos.some((p) => {
          const pDay = String(p?.fecha_pago || '').slice(0, 10);
          return pDay >= startDate && pDay <= endDate;
        })
      ) {
        if (rawId) {
          const k = `g:${rawId}`;
          if (seenIds.has(k)) continue;
          seenIds.add(k);
        }
        sliced.push(ev.isGhost ? ev : { ...ev, isGhost: true });
      }
    }
  }

  return sliced.length > 0 ? sliced : null;
};

export const getCachedRangeEvents = (startDate, endDate, allowCovering = true) => {
  if (!startDate || !endDate) return null;
  const key = `${startDate}_${endDate}`;
  const now = Date.now();
  const exact = _SHARED_RANGE_EVENTS_CACHE.get(key);
  if (exact) {
    if (now - exact.ts <= _SHARED_EVENTS_CACHE_TTL_MS) {
      return exact.data;
    }
    _SHARED_RANGE_EVENTS_CACHE.delete(key);
  }
  if (!allowCovering) return null;

  const cachedDerived = _DERIVED_RANGE_SLICE_CACHE.get(key);
  if (cachedDerived) {
    if (now - cachedDerived.ts <= _SHARED_EVENTS_CACHE_TTL_MS) {
      return cachedDerived.data;
    }
    _DERIVED_RANGE_SLICE_CACHE.delete(key);
  }

  for (const [k, entry] of _SHARED_RANGE_EVENTS_CACHE.entries()) {
    if (now - entry.ts > _SHARED_EVENTS_CACHE_TTL_MS) {
      _SHARED_RANGE_EVENTS_CACHE.delete(k);
      continue;
    }
    const [covStart, covEnd] = k.split('_');
    if (covStart && covEnd && covStart <= startDate && covEnd >= endDate && Array.isArray(entry.data) && entry.data.length > 0) {
      const sliced = sliceEventsForDateRange(entry.data, startDate, endDate);
      if (sliced && sliced.length > 0) {
        _DERIVED_RANGE_SLICE_CACHE.set(key, { ts: now, data: sliced });
        return sliced;
      }
    }
  }

  const allYears = getCachedAllYearsEvents();
  if (Array.isArray(allYears) && allYears.length > 0) {
    const sliced = sliceEventsForDateRange(allYears, startDate, endDate);
    if (sliced && sliced.length > 0) {
      _DERIVED_RANGE_SLICE_CACHE.set(key, { ts: now, data: sliced });
      return sliced;
    }
  }

  const startYear = parseInt(String(startDate).slice(0, 4), 10);
  const endYear = parseInt(String(endDate).slice(0, 4), 10);
  if (!Number.isNaN(startYear) && !Number.isNaN(endYear) && startYear <= endYear && endYear - startYear <= 5) {
    const yearSource = [];
    let allYearsPresent = true;
    for (let y = startYear; y <= endYear; y++) {
      const yEntry = _SHARED_YEAR_EVENTS_CACHE.get(String(y));
      if (!yEntry || now - yEntry.ts > _SHARED_EVENTS_CACHE_TTL_MS || !Array.isArray(yEntry.data) || yEntry.data.length === 0) {
        allYearsPresent = false;
        break;
      }
      for (let i = 0; i < yEntry.data.length; i++) {
        yearSource.push(yEntry.data[i]);
      }
    }
    if (allYearsPresent && yearSource.length > 0) {
      const sliced = sliceEventsForDateRange(yearSource, startDate, endDate);
      if (sliced && sliced.length > 0) {
        _DERIVED_RANGE_SLICE_CACHE.set(key, { ts: now, data: sliced });
        return sliced;
      }
    }
  }

  return null;
};

export const setCachedRangeEvents = (startDate, endDate, data) => {
  if (!startDate || !endDate || !Array.isArray(data) || data.length === 0) return;
  const key = `${startDate}_${endDate}`;
  if (_SHARED_RANGE_EVENTS_CACHE.size > 24 && !_SHARED_RANGE_EVENTS_CACHE.has(key)) {
    const oldestKey = _SHARED_RANGE_EVENTS_CACHE.keys().next().value;
    if (oldestKey !== undefined) _SHARED_RANGE_EVENTS_CACHE.delete(oldestKey);
  }
  _DERIVED_RANGE_SLICE_CACHE.delete(key);
  _SHARED_RANGE_EVENTS_CACHE.set(key, { ts: Date.now(), data });
};

export const fetchRangeEventsCached = (startDate, endDate, fetcher) => {
  if (!startDate || !endDate) return Promise.resolve([]);
  const key = `${startDate}_${endDate}`;
  const exactCached = getCachedRangeEvents(startDate, endDate, false);
  if (exactCached) return Promise.resolve(exactCached);
  const inflight = _INFLIGHT_RANGE_PROMISES.get(key);
  if (inflight) return inflight;
  const reqGen = _analyticsCacheGeneration;
  const p = Promise.resolve()
    .then(() => fetcher(`/api/calendar/events?start_date=${startDate}&end_date=${endDate}&lite=true&summary=true`))
    .then(async (res) => {
      if (res && res.ok) {
        const data = await res.json();
        if (Array.isArray(data)) {
          if (reqGen === _analyticsCacheGeneration) {
            setCachedRangeEvents(startDate, endDate, data);
          }
          return data;
        }
      }
      return [];
    })
    .catch(() => [])
    .finally(() => {
      if (_INFLIGHT_RANGE_PROMISES.get(key) === p) {
        _INFLIGHT_RANGE_PROMISES.delete(key);
      }
    });
  _INFLIGHT_RANGE_PROMISES.set(key, p);
  return p;
};

export const resolveInitialWeeklyFetchWindow = (searchStr = '', refDate = new Date()) => {
  const now = refDate instanceof Date ? refDate : new Date();
  const pad = (n) => String(n).padStart(2, '0');
  const currentMonthStr = `${now.getFullYear()}-${pad(now.getMonth() + 1)}`;

  const calcMonthRange = (monthStr, numWeeks = 12) => {
    const [y, m] = monthStr.split('-');
    const endOfMonth = new Date(parseInt(y, 10), parseInt(m, 10), 0);
    const endDay = endOfMonth.getDay();
    const diffToSunday = endDay === 0 ? 0 : 7 - endDay;
    endOfMonth.setDate(endOfMonth.getDate() + diffToSunday);
    const startOfRolling = new Date(endOfMonth);
    startOfRolling.setDate(startOfRolling.getDate() - (numWeeks * 7 - 1));
    return {
      start: `${startOfRolling.getFullYear()}-${pad(startOfRolling.getMonth() + 1)}-${pad(startOfRolling.getDate())}`,
      end: `${endOfMonth.getFullYear()}-${pad(endOfMonth.getMonth() + 1)}-${pad(endOfMonth.getDate())}`
    };
  };

  const calcPresetRange = (preset, rDate = now) => {
    const today = new Date(rDate.getFullYear(), rDate.getMonth(), rDate.getDate());
    const dow = today.getDay();
    const diffToMonday = dow === 0 ? -6 : 1 - dow;
    const currMonday = new Date(today);
    currMonday.setDate(today.getDate() + diffToMonday);
    const currSunday = new Date(currMonday);
    currSunday.setDate(currMonday.getDate() + 6);
    if (preset === 'wtd') {
      return {
        start: `${currMonday.getFullYear()}-${pad(currMonday.getMonth() + 1)}-${pad(currMonday.getDate())}`,
        end: `${currSunday.getFullYear()}-${pad(currSunday.getMonth() + 1)}-${pad(currSunday.getDate())}`
      };
    }
    const weeksCount = preset === '4w' ? 4 : preset === '8w' ? 8 : preset === '26w' ? 26 : preset === '52w' ? 52 : 12;
    const startMonday = new Date(currSunday);
    startMonday.setDate(currSunday.getDate() - (weeksCount * 7 - 1));
    return {
      start: `${startMonday.getFullYear()}-${pad(startMonday.getMonth() + 1)}-${pad(startMonday.getDate())}`,
      end: `${currSunday.getFullYear()}-${pad(currSunday.getMonth() + 1)}-${pad(currSunday.getDate())}`
    };
  };

  let dateRange = calcMonthRange(currentMonthStr, 12);
  const params = new URLSearchParams(searchStr || '');
  const rawPeriodo = params.get('periodo') || params.get('preset') || params.get('month');
  if (rawPeriodo) {
    let cleanP = rawPeriodo.trim().toLowerCase();
    if (cleanP === '4sem') cleanP = '4w';
    else if (cleanP === '8sem') cleanP = '8w';
    else if (cleanP === '12sem') cleanP = '12w';
    else if (cleanP === '26sem') cleanP = '26w';
    else if (cleanP === '52sem') cleanP = '52w';

    if (['wtd', '4w', '8w', '12w', '26w', '52w'].includes(cleanP)) {
      dateRange =
        cleanP === '12w'
          ? calcMonthRange(currentMonthStr, 12)
          : cleanP === '26w'
            ? calcMonthRange(currentMonthStr, 26)
            : cleanP === '52w'
              ? calcMonthRange(currentMonthStr, 52)
              : calcPresetRange(cleanP, now);
    } else if (/^\d{4}-\d{2}$/.test(rawPeriodo.trim())) {
      dateRange = calcMonthRange(rawPeriodo.trim(), 12);
    }
  }

  const todayStr = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Tijuana',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(now);
  const [ty, tm, td] = todayStr.split('-').map(Number);
  const twelveWeeksAgo = new Date(ty, tm - 1, td);
  twelveWeeksAgo.setDate(twelveWeeksAgo.getDate() - 84);
  const minHistStart = `${twelveWeeksAgo.getFullYear()}-${pad(twelveWeeksAgo.getMonth() + 1)}-${pad(twelveWeeksAgo.getDate())}`;
  const fetchStart =
    dateRange.start <= todayStr && dateRange.end >= todayStr && minHistStart < dateRange.start
      ? minHistStart
      : dateRange.start;
  return { fetchStart, fetchEnd: dateRange.end };
};

export const resolveInitialMonthlyFetchWindows = (searchStr = '', refDate = new Date()) => {
  const now = refDate instanceof Date ? refDate : new Date();
  const pad = (n) => String(n).padStart(2, '0');
  const currentMonthStr = `${now.getFullYear()}-${pad(now.getMonth() + 1)}`;
  const params = new URLSearchParams(searchStr || '');
  const rawPeriodo = params.get('periodo') || params.get('preset') || params.get('month');

  let selectedPreset = '30d';
  let selectedMonth = currentMonthStr;
  if (rawPeriodo) {
    const cleanP = rawPeriodo.trim().toLowerCase();
    if (['mtd', '30d', '60d', '90d'].includes(cleanP)) {
      selectedPreset = cleanP;
      selectedMonth = currentMonthStr;
    } else if (/^\d{4}-\d{2}$/.test(rawPeriodo.trim())) {
      selectedPreset = null;
      selectedMonth = rawPeriodo.trim();
    }
  }

  let start_date;
  let end_date;
  let refY;
  let refM;

  if (selectedPreset) {
    const endObj = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    end_date = `${endObj.getFullYear()}-${pad(endObj.getMonth() + 1)}-${pad(endObj.getDate())}`;
    if (selectedPreset === 'mtd') {
      start_date = `${endObj.getFullYear()}-${pad(endObj.getMonth() + 1)}-01`;
    } else {
      const days = selectedPreset === '30d' ? 30 : selectedPreset === '60d' ? 60 : 90;
      const startObj = new Date(now.getFullYear(), now.getMonth(), now.getDate() - (days - 1));
      start_date = `${startObj.getFullYear()}-${pad(startObj.getMonth() + 1)}-${pad(startObj.getDate())}`;
    }
    refY = now.getFullYear();
    refM = now.getMonth() + 1;
  } else {
    const [y, m] = selectedMonth.split('-').map(Number);
    start_date = `${y}-${pad(m)}-01`;
    const endDateObj = new Date(y, m, 0);
    end_date = `${y}-${pad(m)}-${pad(endDateObj.getDate())}`;
    refY = y;
    refM = m;
  }

  const startHistObj = new Date(refY, refM - 1 - 15, 1);
  const hist_start = `${startHistObj.getFullYear()}-${pad(startHistObj.getMonth() + 1)}-01`;
  const endHistObj = new Date(refY, refM, 0);
  const hist_end = `${endHistObj.getFullYear()}-${pad(endHistObj.getMonth() + 1)}-${pad(endHistObj.getDate())}`;

  return {
    start_date,
    end_date,
    hist_start,
    hist_end
  };
};

const sliceYearEventsFromAllYears = (allEvents, yearKey) => {
  if (!Array.isArray(allEvents) || allEvents.length === 0) return null;
  const yNum = parseInt(String(yearKey), 10);
  if (Number.isNaN(yNum)) return null;
  const yStr = String(yNum);
  const prevDecPrefix = `${yNum - 1}-12`;
  const prevNovPrefix = `${yNum - 1}-11`;
  const nextJanPrefix = `${yNum + 1}-01`;
  const sliced = [];
  for (let i = 0; i < allEvents.length; i++) {
    const ev = allEvents[i];
    if (!ev || typeof ev !== 'object') continue;
    const tStr = String(ev.time || ev.start_time || '');
    if (tStr.startsWith(yStr)) {
      sliced.push(ev);
      continue;
    }
    const monthPrefix = tStr.slice(0, 7);
    if (monthPrefix === prevDecPrefix || monthPrefix === prevNovPrefix || monthPrefix === nextJanPrefix) {
      if (tStr.startsWith(`${yNum + 1}-01-01`) || tStr.startsWith(`${yNum - 1}-12-31`)) {
        sliced.push(ev);
        continue;
      }
      const fd = ev.form_data;
      if (fd && typeof fd === 'object') {
        const pagos = fd.pagos;
        if (Array.isArray(pagos) && pagos.some((p) => p && String(p.fecha_pago || '').startsWith(yStr))) {
          sliced.push({ ...ev, isGhost: true, _targetYear: yStr });
          continue;
        }
      }
    }
  }
  return sliced.length > 0 ? sliced : null;
};

export const getCachedYearEvents = (yearKey) => {
  const k = String(yearKey);
  const entry = _SHARED_YEAR_EVENTS_CACHE.get(k);
  if (entry) {
    if (Date.now() - entry.ts <= _SHARED_EVENTS_CACHE_TTL_MS) {
      return entry.data;
    }
    _SHARED_YEAR_EVENTS_CACHE.delete(k);
  }
  const allYears = getCachedAllYearsEvents();
  if (Array.isArray(allYears) && allYears.length > 0) {
    const derived = sliceYearEventsFromAllYears(allYears, k);
    if (derived && derived.length > 0) {
      setCachedYearEvents(k, derived);
      return derived;
    }
  }
  return null;
};

export const setCachedYearEvents = (yearKey, data) => {
  if (!Array.isArray(data) || data.length === 0) return;
  const k = String(yearKey);
  if (_SHARED_YEAR_EVENTS_CACHE.size > 12 && !_SHARED_YEAR_EVENTS_CACHE.has(k)) {
    const oldestKey = _SHARED_YEAR_EVENTS_CACHE.keys().next().value;
    if (oldestKey !== undefined) _SHARED_YEAR_EVENTS_CACHE.delete(oldestKey);
  }
  _DERIVED_RANGE_SLICE_CACHE.clear();
  _SHARED_YEAR_EVENTS_CACHE.set(k, { ts: Date.now(), data });
};

export const getCachedAllYearsEvents = () => {
  if (!_SHARED_ALL_YEARS_EVENTS_CACHE) return null;
  if (Date.now() - _SHARED_ALL_YEARS_EVENTS_CACHE.ts > _SHARED_EVENTS_CACHE_TTL_MS) {
    _SHARED_ALL_YEARS_EVENTS_CACHE = null;
    return null;
  }
  return _SHARED_ALL_YEARS_EVENTS_CACHE.data;
};

export const setCachedAllYearsEvents = (data) => {
  if (!Array.isArray(data) || data.length === 0) return;
  _DERIVED_RANGE_SLICE_CACHE.clear();
  _SHARED_ALL_YEARS_EVENTS_CACHE = { ts: Date.now(), data };
};

export const fetchYearEventsCached = (yearKey, fetcher) => {
  const k = String(yearKey);
  const cached = getCachedYearEvents(k);
  if (cached) return Promise.resolve(cached);
  const inflight = _INFLIGHT_YEAR_PROMISES.get(k);
  if (inflight) return inflight;
  const reqGen = _analyticsCacheGeneration;
  const p = Promise.resolve()
    .then(() => fetcher(`/api/calendar/events?year=${k}&lite=true&summary=true`))
    .then(async (res) => {
      if (res && res.ok) {
        const data = await res.json();
        if (Array.isArray(data)) {
          if (reqGen === _analyticsCacheGeneration) {
            setCachedYearEvents(k, data);
          }
          return data;
        }
      }
      return null;
    })
    .catch(() => null)
    .finally(() => {
      if (_INFLIGHT_YEAR_PROMISES.get(k) === p) {
        _INFLIGHT_YEAR_PROMISES.delete(k);
      }
    });
  _INFLIGHT_YEAR_PROMISES.set(k, p);
  return p;
};

export const fetchAllYearsEventsCached = (fetcher) => {
  const cached = getCachedAllYearsEvents();
  if (cached) return Promise.resolve(cached);
  if (_INFLIGHT_ALL_YEARS_PROMISE) return _INFLIGHT_ALL_YEARS_PROMISE;
  const reqGen = _analyticsCacheGeneration;
  const p = Promise.resolve()
    .then(() => fetcher('/api/calendar/events?all_years=true&lite=true&summary=true'))
    .then(async (res) => {
      if (res && res.ok) {
        const data = await res.json();
        if (Array.isArray(data)) {
          if (reqGen === _analyticsCacheGeneration) {
            setCachedAllYearsEvents(data);
          }
          return data;
        }
      }
      return null;
    })
    .catch(() => null)
    .finally(() => {
      if (_INFLIGHT_ALL_YEARS_PROMISE === p) {
        _INFLIGHT_ALL_YEARS_PROMISE = null;
      }
    });
  _INFLIGHT_ALL_YEARS_PROMISE = p;
  return p;
};

const _GHOST_TARGET_YEAR_WEAK_CACHE = typeof WeakMap !== 'undefined' ? new WeakMap() : null;
const _MERGED_YEARS_ARRAY_CACHE = new Map();

const getGhostEventForTargetYear = (ev, yStr) => {
  if (ev._targetYear === yStr) return ev;
  if (!_GHOST_TARGET_YEAR_WEAK_CACHE) return { ...ev, _targetYear: yStr };
  let byYear = _GHOST_TARGET_YEAR_WEAK_CACHE.get(ev);
  if (!byYear) {
    byYear = new Map();
    _GHOST_TARGET_YEAR_WEAK_CACHE.set(ev, byYear);
  }
  let tagged = byYear.get(yStr);
  if (!tagged) {
    tagged = { ...ev, _targetYear: yStr };
    byYear.set(yStr, tagged);
  }
  return tagged;
};

export const hasAllSelectedYearsCached = (yearsList, localCacheMap = {}) => {
  if (!Array.isArray(yearsList) || yearsList.length === 0) return false;
  for (let i = 0; i < yearsList.length; i++) {
    const yStr = String(yearsList[i]);
    const arr = (localCacheMap && localCacheMap[yStr]) || getCachedYearEvents(yStr);
    if (!Array.isArray(arr) || arr.length === 0) {
      return false;
    }
  }
  return true;
};

export const mergeEventsForSelectedYears = (yearsList, localCacheMap = {}) => {
  if (!Array.isArray(yearsList) || yearsList.length === 0) return [];
  if (yearsList.length === 1) {
    const yStr = String(yearsList[0]);
    return (localCacheMap && localCacheMap[yStr]) || getCachedYearEvents(yStr) || [];
  }
  const sortedAsc = Array.from(new Set(yearsList.map(String))).sort();
  const cacheKey = sortedAsc.join(',');
  const yearArrays = sortedAsc.map(
    (yStr) => (localCacheMap && localCacheMap[yStr]) || getCachedYearEvents(yStr) || []
  );

  const prev = _MERGED_YEARS_ARRAY_CACHE.get(cacheKey);
  if (prev && prev.yearArrays.length === yearArrays.length) {
    let sameInputs = true;
    for (let i = 0; i < yearArrays.length; i++) {
      if (prev.yearArrays[i] !== yearArrays[i] || prev.yearLengths[i] !== yearArrays[i].length) {
        sameInputs = false;
        break;
      }
    }
    if (sameInputs) {
      return prev.combined;
    }
  }

  const combined = [];
  const seenNonGhostIds = new Set();
  const seenGhostKeys = new Set();

  for (let i = 0; i < sortedAsc.length; i++) {
    const yStr = sortedAsc[i];
    const yearArr = yearArrays[i];
    for (let j = 0; j < yearArr.length; j++) {
      const ev = yearArr[j];
      if (!ev || typeof ev !== 'object') continue;
      const rawId = ev.id || ev.google_event_id;
      if (ev.isGhost) {
        const gKey = rawId ? `${rawId}::ghost::${yStr}` : null;
        if (gKey && seenGhostKeys.has(gKey)) continue;
        if (gKey) seenGhostKeys.add(gKey);
        combined.push(getGhostEventForTargetYear(ev, yStr));
      } else {
        if (rawId && seenNonGhostIds.has(rawId)) continue;
        if (rawId) seenNonGhostIds.add(rawId);
        combined.push(ev);
      }
    }
  }

  if (_MERGED_YEARS_ARRAY_CACHE.size > 16) {
    _MERGED_YEARS_ARRAY_CACHE.clear();
  }
  _MERGED_YEARS_ARRAY_CACHE.set(cacheKey, {
    yearArrays,
    yearLengths: yearArrays.map((a) => a.length),
    combined
  });
  return combined;
};

export const clearAnalyticsEventsCache = () => {
  _analyticsCacheGeneration += 1;
  _SHARED_RANGE_EVENTS_CACHE.clear();
  _DERIVED_RANGE_SLICE_CACHE.clear();
  _INFLIGHT_RANGE_PROMISES.clear();
  _SHARED_YEAR_EVENTS_CACHE.clear();
  _INFLIGHT_YEAR_PROMISES.clear();
  _MERGED_YEARS_ARRAY_CACHE.clear();
  _SHARED_ALL_YEARS_EVENTS_CACHE = null;
  _INFLIGHT_ALL_YEARS_PROMISE = null;
};

if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
  window.addEventListener('gema:appointment-updated', () => {
    clearAnalyticsEventsCache();
  });
}
